﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// The default control for an external login provider.

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import { createUseStyles } from "react-jss";
import React from "react";
import { ActionLink, RouteLink } from "~base/Components/Primitives/Link";
import { LoginPageCustomizations } from "../Customization";
import { ConsilioBaseTheme } from "~base/Styling/ConsilioTheme";
import { Button } from "~base/Components/Atoms/Button";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

interface ExternalLoginProviderProps {
    name: string;
    customizations: LoginPageCustomizations;
}

//-------------------------------------------------------------------------------------
// Functions
//-------------------------------------------------------------------------------------

export const getExternalLoginUrl = (name: string) => {
    const separator = window.location.href.includes("?") ? "&" : "?";
    return window.location.href + separator + "provider=" + name;
};

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const ExternalLoginProvider: React.FunctionComponent<
    ExternalLoginProviderProps
> = ({ name, customizations }) => {
    const classes = useAdditionalStyles(customizations);

    const target = getExternalLoginUrl(name);

    const control = customizations.externalLoginControls?.[name];

    if (control) {
        return control;
    }

    const displayName =
        customizations.externalLoginDisplayNames?.[name] ?? name;

    return (
        <Button
            testIdentifier={`ExternalLoginProvider${name}`}
            onClick={() => (window.location.href = target)}
            className={classes.button}
        >
            {displayName}
        </Button>
    );
};

//-------------------------------------------------------------------------------------
// Styles
//-------------------------------------------------------------------------------------

const useAdditionalStyles = (customizations?: LoginPageCustomizations) =>
    createUseStyles(
        (theme: ConsilioBaseTheme) => {
            return {
                button: {
                    marginBottom: "20px",
                    minWidth: "306px",
                },
            };
        },
        { name: "ExternalLoginProvider" }
    )();
